"use client";

import * as Sentry from "@sentry/nextjs";
import mapboxgl, { GeoJSONSource, Map as MapboxMap, Popup } from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import {
  buildPopupHTML,
  getPointCoordinates,
  pickNearestStop,
  StopFeature,
  updatePopupOffset,
  wrapLngLat,
} from "@/utils/mapHelpers";

interface TourMapProps {
  setFocusedId?: (id: number) => void;
  tilesetId: string;
  showMap: boolean;
  setShowMap: (v: boolean) => void;
}

interface TourMapHandle {
  setNavigationMode: (mode: boolean) => void;
  flyToStop: (id: number) => void;
}

const accessToken = process.env.NEXT_PUBLIC_MAPBOX_TOKEN;
mapboxgl.accessToken = accessToken;

const TourMap = forwardRef<TourMapHandle, TourMapProps>(
  ({ setFocusedId, tilesetId, showMap, setShowMap }, ref) => {
    const drivingMode = true;
    const [mapLoaded, setMapLoaded] = useState(false);
    /** This browser won't give Mapbox a WebGL context, so there is no map to show. */
    const [webglFailed, setWebglFailed] = useState(false);
    const mapContainer = useRef<HTMLDivElement | null>(null);
    const map = useRef<MapboxMap | null>(null);
    const popupRef = useRef<Popup | null>(null);
    const lastPopupIdRef = useRef<string | number | null>(null);
    const allFeaturesRef = useRef<StopFeature[]>([]);
    const routeDataRef = useRef<GeoJSON.Feature<GeoJSON.LineString> | null>(
      null,
    );
    const hasCachedRef = useRef(false);

    const buildDrivingRoute = async () => {
      if (!map.current) return;

      const features = allFeaturesRef.current;
      if (!features || !features.length) {
        console.warn("buildDrivingRoute: no features cached yet");
        return;
      }

      // Build minimal payload to send to the server
      const stops = features
        .filter((f) => f.geometry?.type === "Point")
        .map((f) => ({
          order: Number(f.properties.order),
          transport: f.properties.transport || "car",
          coords: f.geometry.coordinates,
          date: f.properties.date || null,
          itinerary: f.properties.itinerary
            ? Number(f.properties.itinerary)
            : null,
          waypoint: f.properties.waypoint === true || f.properties.waypoint === "true",
        }))
        .filter((s) => Number.isFinite(s.order));

      try {
        const res = await fetch(`/api/mapbox?tilesetId=${tilesetId}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ tilesetId, stops }),
        });

        if (!res.ok) {
          console.error("Server route build failed with status", res.status);
          return;
        }

        const data = await res.json();
        const mainCoords = data.main;
        const footCoords = data.foot;
        const boatCoords = data.boat as [number, number][][];

        if (!mainCoords || !mainCoords.length) {
          console.warn("buildDrivingRoute: empty coordinates from server");
          return;
        }

        const routeFeature = {
          type: "Feature" as const,
          geometry: {
            type: "LineString",
            coordinates: mainCoords,
          },
          properties: {},
        };

        routeDataRef.current =
          routeFeature as GeoJSON.Feature<GeoJSON.LineString>;

        const sourceId = "driving-route";
        const layerId = "driving-route-layer";

        const drivingSource = map.current!.getSource(sourceId) as
          | GeoJSONSource
          | undefined;
        if (drivingSource) {
          drivingSource.setData(routeFeature as GeoJSON.Feature);
        } else {
          map.current!.addSource(sourceId, {
            type: "geojson",
            data: routeFeature as GeoJSON.Feature,
          });

          map.current!.addLayer({
            id: layerId,
            type: "line",
            source: sourceId,
            layout: {
              "line-join": "round",
              "line-cap": "round",
            },
            paint: {
              "line-color": "#00638a",
              "line-width": 4,
            },
          });
        }

        // Add foot route layers
        Object.keys(footCoords || {}).forEach((day) => {
          const footSourceId = `foot-route-${day}`;
          const footLayerId = `foot-route-layer-${day}`;
          const coords = footCoords[day];

          const footFeature = {
            type: "Feature" as const,
            geometry: { type: "LineString", coordinates: coords },
            properties: {},
          };

          const footSource = map.current!.getSource(footSourceId) as
            | GeoJSONSource
            | undefined;
          if (footSource) {
            footSource.setData(footFeature as GeoJSON.Feature);
          } else {
            map.current!.addSource(footSourceId, {
              type: "geojson",
              data: footFeature as GeoJSON.Feature,
            });

            map.current!.addLayer({
              id: footLayerId,
              type: "line",
              source: footSourceId,
              layout: { "line-join": "round", "line-cap": "round" },
              paint: {
                "line-color": "#22c55e",
                "line-width": 3,
                "line-dasharray": [1, 1.5],
              },
            });
          }
        });
        // Add boat route layer
        if (boatCoords && boatCoords.length) {
          const boatFeature = {
            type: "Feature" as const,
            geometry: {
              type: "MultiLineString",
              coordinates: boatCoords,
            },
            properties: {},
          };

          const boatSourceId = "boat-route";
          const boatLayerId = "boat-route-layer";

          const boatSource = map.current!.getSource(boatSourceId) as
            | GeoJSONSource
            | undefined;
          if (boatSource) {
            boatSource.setData(boatFeature as GeoJSON.Feature);
          } else {
            map.current!.addSource(boatSourceId, {
              type: "geojson",
              data: boatFeature as GeoJSON.Feature,
            });

            map.current!.addLayer({
              id: boatLayerId,
              type: "line",
              source: boatSourceId,
              layout: { "line-join": "round", "line-cap": "round" },
              paint: {
                "line-color": "#0ea5e9",
                "line-width": 3,
                "line-dasharray": [2, 1],
              },
            });
          }
        }
      } catch (e) {
        console.error("buildDrivingRoute: server error", e);
      }
    };

    useEffect(() => {
      if (!showMap) return;
      if (map.current) return; // initialize map only once

      (async () => {
        try {
          map.current = new mapboxgl.Map({
            container: mapContainer.current!,
            style: "mapbox://styles/mapbox/streets-v12",
            center: [0, 0],
            zoom: 1,
            attributionControl: false,
          });
        } catch (error) {
          // The click below already asked supported(), so reaching this means
          // the driver said yes and then refused the context anyway. Before it
          // was caught, the throw left this un-awaited IIFE as an unhandled
          // rejection and the visitor watched the blurred placeholder forever:
          // `mapLoaded` never flipped, and nothing else was ever shown.
          console.error("TourMap: map failed to initialize", error);
          setWebglFailed(true);
          return;
        }

        // Removed map.current._tourSourceLayer assignment

        // Named, and registered with a .catch() below, rather than handed to
        // on() as an async callback: mapbox drops the promise such a callback
        // returns, so everything awaited in here — the /api/mapbox/stops fetch
        // above all — used to fail as an unhandled rejection that named no call
        // site. Same reason the newsletter POST in Footer.tsx catches its own.
        const setUpMap = async () => {
          setMapLoaded(true);
          // Removed adding vector tileset source "tour-tileset" and layers "route-line" and "stops-circle"

          map.current!.addControl(
            new mapboxgl.NavigationControl({
              showCompass: true,
              showZoom: true,
              visualizePitch: true,
            }),
            "top-right",
          );

          map.current!.addSource("mapbox-dem", {
            type: "raster-dem",
            url: "mapbox://mapbox.terrain-rgb",
            tileSize: 512,
            maxzoom: 14,
          });

          map.current!.setTerrain({ source: "mapbox-dem", exaggeration: 1.4 });

          map.current!.addLayer({
            id: "sky",
            type: "sky",
            paint: {
              "sky-type": "atmosphere",
              "sky-atmosphere-sun": [0.0, 0.0],
              "sky-atmosphere-sun-intensity": 15,
            },
          });

          const stopsRes = await fetch(
            `/api/mapbox/stops?tilesetId=${tilesetId}`,
          );

          if (!stopsRes.ok) {
            console.error("Stops fetch failed", stopsRes.status);
            return;
          }

          const stopsJson = await stopsRes.json();

          if (!Array.isArray(stopsJson)) {
            console.error("Stops response is not an array", stopsJson);
            return;
          }

          allFeaturesRef.current = stopsJson.map((f) => ({
            properties: f.properties,
            geometry: {
              type: "Point",
              coordinates: f.geometry.coordinates as [number, number],
            },
          }));

          hasCachedRef.current = true;

          if (drivingMode) {
            buildDrivingRoute();
          }

          map.current!.addSource("tour-stops", {
            type: "geojson",
            data: {
              type: "FeatureCollection",
              features: stopsJson.filter(
                (f) =>
                  f.properties.waypoint !== true &&
                  f.properties.waypoint !== "true",
              ),
            },
          });

          // Larger invisible hit area for easier hover/click
          map.current!.addLayer({
            id: "stops-hit",
            type: "circle",
            source: "tour-stops",
            paint: {
              "circle-radius": 14,
              "circle-color": "rgba(0,0,0,0)",
              "circle-stroke-width": 0,
            },
          });

          // Visible circle marker
          map.current!.addLayer({
            id: "stops-circle",
            type: "circle",
            source: "tour-stops",
            paint: {
              "circle-radius": 7,
              "circle-color": "#ef4444",
              "circle-stroke-width": 2,
              "circle-stroke-color": "#ffffff",
            },
          });

          map.current!.easeTo({
            pitch: 0,
            bearing: -20,
            duration: 2000,
          });

          popupRef.current = new mapboxgl.Popup({
            closeButton: false,
            closeOnClick: false,
            offset: [0, -95],
            anchor: "top",
            className: "tourmap-popup",
            closeOnMove: false,
          });

          // Hover popup handlers
          map.current!.on("mouseenter", "stops-hit", (e) => {
            map.current!.getCanvas()!.style.cursor = "pointer";

            const feature = pickNearestStop(e, map.current!);
            if (!feature) return;

            const rawCoords = getPointCoordinates(feature.geometry);
            const coordinates = wrapLngLat(rawCoords, e.lngLat.lng);
            const props = feature.properties;

            const currentId =
              props.id ??
              props.itinerary ??
              props.order ??
              JSON.stringify(coordinates);
            lastPopupIdRef.current = currentId;

            const coords = coordinates as [number, number];
            popupRef
              .current!.setLngLat(coords)
              .setHTML(buildPopupHTML(props))
              .addTo(map.current!);

            updatePopupOffset(popupRef.current!);
          });

          map.current!.on("mousemove", "stops-hit", (e) => {
            const feature = pickNearestStop(e, map.current!);
            if (!feature) return;

            const rawCoords = getPointCoordinates(feature.geometry);
            const coordinates = wrapLngLat(rawCoords, e.lngLat.lng);
            const props = feature.properties;

            const currentId =
              props.id ??
              props.itinerary ??
              props.order ??
              JSON.stringify(coordinates);

            const coords = coordinates as [number, number];
            // If we moved to a different stop, update content as well
            if (lastPopupIdRef.current !== currentId) {
              lastPopupIdRef.current = currentId;

              popupRef
                .current!.setLngLat(coords)
                .setHTML(buildPopupHTML(props));

              updatePopupOffset(popupRef.current!);
            } else {
              // Same stop, just track the lng/lat
              popupRef.current!.setLngLat(coords);
            }
          });

          map.current!.on("mouseleave", "stops-hit", () => {
            map.current!.getCanvas()!.style.cursor = "";
            lastPopupIdRef.current = null;
            if (popupRef.current) popupRef.current.remove();
          });

          map.current!.on("click", "stops-hit", (e) => {
            if (!e.features || !e.features.length) {
              return;
            }

            // Always pick nearest
            const feature = pickNearestStop(e, map.current!);
            if (!feature) {
              return;
            }

            const props = feature.properties;
            const coordinates = getPointCoordinates(feature.geometry);
            const coords = coordinates as [number, number];
            const isMobile =
              typeof window !== "undefined" && window.innerWidth < 768;

            if (isMobile) {
              // MOBILE → Destroy + recreate popup to force update
              if (popupRef.current) popupRef.current.remove();
              popupRef.current = new mapboxgl.Popup({
                closeButton: false,
                closeOnClick: false,
                offset: [0, -95],
                anchor: "top",
                className: "tourmap-popup",
                closeOnMove: false,
              });

              popupRef
                .current!.setLngLat(coords)
                .setHTML(buildPopupHTML(props))
                .addTo(map.current!);

              updatePopupOffset(popupRef.current!);
            } else {
              // DESKTOP → Normal popup update (reuse works correctly)
              popupRef
                .current!.setLngLat(coords)
                .setHTML(buildPopupHTML(props));

              updatePopupOffset(popupRef.current!);
            }

            if (setFocusedId) {
              const itinerary = parseInt(String(props.itinerary ?? ""), 10);

              if (!Number.isNaN(itinerary) && itinerary > 0) {
                setFocusedId(itinerary);
              } else {
                console.log("Invalid itinerary value, ignoring");
              }
            }
          });

          // Fit to bounds of the whole trip so all tiles load once
          // Compute dynamic bounding box from all stops
          const all = stopsJson.filter((f) => f.geometry?.type === "Point");
          if (all && all.length) {
            const lons = all.map(
              (f) =>
                (f.geometry as unknown as { coordinates: [number, number] })
                  .coordinates[0],
            );
            const lats = all.map(
              (f) =>
                (f.geometry as unknown as { coordinates: [number, number] })
                  .coordinates[1],
            );
            const minLon = Math.min(...lons);
            const maxLon = Math.max(...lons);
            const minLat = Math.min(...lats);
            const maxLat = Math.max(...lats);

            map.current!.fitBounds(
              [
                [minLon, minLat],
                [maxLon, maxLat],
              ],
              { padding: 40, duration: 0 },
            );
          }

          // After map has finished loading those tiles, cache all features ONCE
        };

        map.current.on("load", () => {
          setUpMap().catch((error) => {
            // The map itself is already on screen by now (setMapLoaded fires
            // first), so this leaves a visitor looking at a map with no stops
            // on it. Nothing to do about that from here, but it is ours and it
            // should be visible.
            Sentry.captureException(error, {
              level: "error",
              tags: { source: "tour-map-setup" },
              extra: { tilesetId },
            });
          });
        });
      })();

      return () => {
        if (map.current) map.current.remove();
      };
    }, [showMap, tilesetId]);

    useEffect(() => {
      if (!map.current) return;

      const layerId = "driving-route-layer";

      if (!drivingMode) {
        if (map.current!.getLayer(layerId)) {
          map.current!.setLayoutProperty(layerId, "visibility", "none");
        }
        return;
      }

      if (map.current!.getLayer(layerId)) {
        map.current!.setLayoutProperty(layerId, "visibility", "visible");
        return;
      }

      if (allFeaturesRef.current && allFeaturesRef.current.length) {
        buildDrivingRoute();
      }
    }, [drivingMode]);

    useImperativeHandle(
      ref,
      () =>
        ({
          setNavigationMode(mode: boolean) {
            const enable = Boolean(mode);
            if (!map.current) return;

            const layerId = "driving-route-layer";

            if (!enable) {
              if (map.current!.getLayer(layerId)) {
                map.current!.setLayoutProperty(layerId, "visibility", "none");
              }
              return;
            }

            if (map.current!.getLayer(layerId)) {
              map.current!.setLayoutProperty(layerId, "visibility", "visible");
              return;
            }

            if (allFeaturesRef.current && allFeaturesRef.current.length) {
              buildDrivingRoute();
            }
          },
          flyToStop(id: number) {
            if (!map.current) return;

            const features =
              allFeaturesRef.current && allFeaturesRef.current.length
                ? allFeaturesRef.current
                : map.current!.queryRenderedFeatures({
                    layers: ["stops-circle"],
                  });

            if (!features || !features.length) {
              console.warn("flyToStop: no features available");
              return;
            }

            let targetFeature = null;

            // Interpret id as itinerary number (1-based)
            const itineraryNum = parseInt(id as any, 10);

            if (!Number.isNaN(itineraryNum)) {
              const group = features
                .filter(
                  (f) =>
                    parseInt(String(f.properties?.itinerary ?? ""), 10) ===
                    itineraryNum,
                )
                .sort(
                  (a, b) =>
                    parseFloat(String(a.properties?.order ?? "")) -
                    parseFloat(String(b.properties?.order ?? "")),
                );

              if (group.length) {
                targetFeature = group[0]; // first stop in itinerary, even if foot
              }
            }

            // Fallback: interpret id as raw order
            if (!targetFeature) {
              targetFeature = features.find(
                (f) =>
                  parseFloat(String(f.properties?.order ?? "")) ===
                  parseFloat(String(id)),
              );
            }

            if (!targetFeature) {
              console.warn("flyToStop: no feature found for id:", id);
              return;
            }

            const coordinates = (
              targetFeature.geometry as unknown as {
                coordinates: [number, number];
              }
            ).coordinates;
            const coords = coordinates as [number, number];
            map.current!.flyTo({
              center: coords,
              zoom: 10,
              speed: 0.7,
              curve: 1.2,
              essential: true,
              pitch: 60,
            });
          },
        }) as TourMapHandle,
    );

    // Before the visitor asks for the map, and again when this browser turned
    // out not to be able to draw it: same blurred placeholder either way, with
    // the button swapped for an explanation. The itinerary above the map
    // already lists every stop, so there is nothing to route around — the
    // visitor only needs to know the picture isn't coming and why.
    if (!showMap || webglFailed) {
      return (
        <div
          id='tourmap-preview'
          className='h-[50vh] w-full md:w-[90%] px-0 md:px-8 lg:px-16 md:h-120'
          style={{
            borderRadius: "12px",
            overflow: "hidden",
            position: "relative",
            backgroundColor: "#e5e7eb",
          }}
        >
          <div style={{ position: "absolute", inset: 0 }}>
            <img
              src='/assets/map-preview.png'
              alt='Map preview'
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                filter: "blur(2px) brightness(0.85)",
                transform: "scale(1.1)",
              }}
            />
          </div>
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 10,
            }}
          >
            {webglFailed ? (
              <p
                style={{
                  maxWidth: "22rem",
                  padding: "12px 20px",
                  background: "rgba(0, 0, 0, 0.65)",
                  color: "white",
                  borderRadius: "8px",
                  fontSize: "15px",
                  lineHeight: 1.5,
                  textAlign: "center",
                }}
              >
                Mapu sa v tomto prehliadači nepodarilo zobraziť — chýba mu
                podpora WebGL, ktorú býva potrebné zapnúť v nastaveniach
                (hardvérové zrýchlenie). Všetky zastávky výletu nájdete v
                itinerári vyššie.
              </p>
            ) : (
              <button
                style={{
                  padding: "12px 20px",
                  background: "#00638a",
                  color: "white",
                  borderRadius: "8px",
                  fontSize: "18px",
                  cursor: "pointer",
                  border: "none",
                }}
                onClick={() => {
                  // Mapbox is WebGL or nothing, and a real share of visitors
                  // have no WebGL: hardware acceleration switched off, a driver
                  // on the browser's blocklist, a locked-down work machine.
                  // Asking before the switch means they keep the placeholder
                  // they were already looking at instead of losing it to a map
                  // that can never draw.
                  if (!mapboxgl.supported()) {
                    setWebglFailed(true);
                    return;
                  }
                  setShowMap(true);
                }}
              >
                Zobraziť mapu
              </button>
            )}
          </div>
        </div>
      );
    }

    return (
      <div
        id='tourmap-wrapper'
        className='h-[50vh] w-full md:h-120 md:w-[90%] px-0 md:px-8 lg:px-16'
        style={{ position: "relative", borderRadius: "12px" }}
      >
        <div
          ref={mapContainer}
          id='tourmap-container'
          style={{
            position: "absolute",
            inset: 0,
            borderRadius: "12px",
            opacity: mapLoaded ? 1 : 0,
            transition: "opacity 0.8s ease",
          }}
        />

        {!mapLoaded && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              borderRadius: "12px",
              overflow: "hidden",
              backgroundColor: "#e5e7eb",
              transition: "opacity 0.6s ease",
            }}
          >
            <img
              src='/assets/map-preview.png'
              alt='Map preview'
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                filter: "blur(12px) brightness(0.85)",
                transform: "scale(1.1)",
              }}
            />
          </div>
        )}
        <style jsx global>{`
          .tourmap-popup .mapboxgl-popup-content {
            background: transparent !important;
            box-shadow: none !important;
            border: none !important;
            padding: 0 !important;
          }
          .tourmap-popup .mapboxgl-popup-tip {
            display: none !important;
          }
          .tourmap-popup {
            pointer-events: none !important;
          }
          .mapboxgl-popup,
          .mapboxgl-popup * {
            pointer-events: none !important;
          }
          @media (max-width: 768px) {
            #tourmap-container {
              height: 50vh !important;
            }
          }
        `}</style>
      </div>
    );
  },
);

export default TourMap;
